import {
  ErrorText,
  FieldContainer,
  FieldGroupContainer,
  FormContainer,
  Label,
  StyledButtonGroup,
} from "../styled";
import Select from "@8x8/oxygen-select";
import { FreeformAddressSelector } from "@/react/features/support-tools/siptrunk/dids/FreeformAddressSelector";
import { Button } from "@/react/styled";
import React, { useEffect, useState } from "react";
import {
  ADDRESS_MODAL_ACTIONS,
  COUNTRY_CODES,
} from "@/react/features/support-tools/siptrunk/constants";
import { useGetAllDidsQuery } from "@/react/services/common/didsApi";
import { cloneDeep, set } from "lodash";
import styled from "styled-components";
import Input from "@8x8/oxygen-input";
import Modal, { ModalHeader } from "@8x8/oxygen-modal";
import { IconButton } from "@8x8/oxygen-icon-button";
import PlusCircleIcon from "@8x8/oxygen-icon/icons/PlusCircleIcon";
import { PencilInBoxIcon } from "@8x8/oxygen-icon";
import { useBindTrunkGatewayDidsMutation } from "@/react/features/support-tools/siptrunk/dids/api";
import { errorNotification } from "@/react/notifications";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";

const HorizontalLine = styled.hr`
  width: 100%;
  margin: 0;
`;

const AddressHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AddressActions = styled.div`
  display: flex;
`;

const INITIAL_COMMON_DETAILS = Object.freeze({
  addressId: null,
  emergencyDetails: { firstName: "", lastName: "", email: "" },
});

export const BulkAddDidsForm = ({ trunkId, addresses, onClose }) => {
  const { data: availableDids, isLoading } = useGetAllDidsQuery();

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressModalOpenFor, setAddressModalOpenFor] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [filteredDids, setFilteredDids] = useState([]);
  const [selectedDids, setSelectedDids] = useState([]);
  const [commonDetails, setCommonDetails] = useState(INITIAL_COMMON_DETAILS);
  const [addressOptions, setAddressOptions] = useState([]);
  const [bindErrors, setBindErrors] = useState([]);
  const [bindDids, { isLoading: isBindingDids }] =
    useBindTrunkGatewayDidsMutation();

  useEffect(() => {
    setFilteredDids(
      (availableDids || [])
        .filter((did) =>
          selectedCountry ? did.country === selectedCountry.value : true
        )
        .map((did) => ({
          ...did,
          label: did.phoneNumber,
          value: did.uuid,
        }))
    );
  }, [availableDids, selectedCountry]);

  useEffect(() => {
    setAddressOptions(
      addresses.map((address) => ({
        label: address.freeform,
        value: address.id,
      }))
    );
  }, [addresses]);

  const handleSave = () => {
    const didsToBind = selectedDids.map((did) => ({
      didId: did.uuid,
      phoneNumber: did.phoneNumber,
      ...commonDetails,
    }));
    bindDids({ trunkId, dids: didsToBind })
      .then((res) => {
        if (res.error) {
          //TODO: Map errors to inline validations
          notify({
            content: `DIDs could not be binded: ${res.error?.data?.message}`,
            type: TYPE.error,
            duration: DURATION.INFINITE,
          });
        } else {
          const failedBinds = res.data
            ?.filter(
              (responseEntity) => responseEntity.operationResult?.failure
            )
            .map((responseEntity) => ({
              id: responseEntity.identifier,
              message: responseEntity.operationResult.failure.message,
            }));
          setBindErrors(failedBinds);
          if (failedBinds.length > 0) {
            notify({
              content: `${didsToBind.length - failedBinds.length} out of ${
                didsToBind.length
              } DIDs binded successfully.`,
              type: TYPE.warning,
              duration: 5000,
            });
          } else {
            notify({
              content: `DIDs binded successfully.`,
              type: TYPE.success,
              duration: 5000,
            });
            onClose();
          }
        }
      })
      .catch(errorNotification);
  };

  const handleReset = () => {
    setSelectedCountry(null);
    setSelectedDids([]);
    setCommonDetails(INITIAL_COMMON_DETAILS);
  };

  const updateCommonDetails = (path, value) => {
    const newCommonDetails = cloneDeep(commonDetails);
    set(newCommonDetails, path, value);
    setCommonDetails(newCommonDetails);
  };

  const saveAddress = (address) => {
    const { id: addressId, displayForm } = address;
    setCommonDetails({ ...commonDetails, addressId });
    setAddressOptions([
      ...addressOptions,
      { value: addressId, label: displayForm },
    ]);
    setSelectedAddress({
      value: addressId,
      label: displayForm,
    });
    setAddressModalOpenFor(null);
  };

  const handleAddressSelection = (opt) => {
    setSelectedAddress(opt);
    setCommonDetails({ ...commonDetails, addressId: opt?.value });
  };

  return (
    <>
      {isBindingDids && <OverlaySpinner />}
      {addressModalOpenFor && (
        <Modal
          onClose={() => setAddressModalOpenFor(null)}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
        >
          <ModalHeader
            title={"Address Validation"}
            onClose={() => setAddressModalOpenFor(null)}
          />
          <FreeformAddressSelector
            selectedAddress={
              selectedAddress &&
              (addresses || []).find(
                (address) => address.id === selectedAddress.value
              )
            }
            action={addressModalOpenFor}
            onSave={saveAddress}
          />
        </Modal>
      )}

      <FormContainer>
        <FieldContainer>
          <Label>DID Country Filter</Label>
          <Select
            value={selectedCountry}
            options={COUNTRY_CODES}
            onChange={(opt) => setSelectedCountry(opt)}
            isClearable={true}
          />
        </FieldContainer>
        <FieldContainer>
          <Label>
            {selectedDids.length} selected DIDs / {filteredDids?.length}{" "}
            available
          </Label>
          <Select
            isMulti={true}
            isLoading={isLoading}
            options={filteredDids}
            value={selectedDids}
            onChange={(opt) => setSelectedDids(opt)}
            isDisabled={isLoading}
          />
        </FieldContainer>
        <HorizontalLine />
        <FieldContainer>
          <AddressHeaderContainer>
            <Label>Emergency Address</Label>
            <AddressActions>
              <IconButton
                onClick={() =>
                  setAddressModalOpenFor(ADDRESS_MODAL_ACTIONS.CREATE)
                }
              >
                <PlusCircleIcon />
              </IconButton>
              <IconButton
                isDisabled={!selectedAddress}
                onClick={() =>
                  setAddressModalOpenFor(ADDRESS_MODAL_ACTIONS.EDIT)
                }
              >
                <PencilInBoxIcon />
              </IconButton>
            </AddressActions>
          </AddressHeaderContainer>
          <Select
            options={addressOptions}
            value={selectedAddress}
            onChange={handleAddressSelection}
          />
        </FieldContainer>
        <Label>Emergency Contact Details</Label>
        <HorizontalLine />
        <FieldGroupContainer marginTop="0">
          <FieldContainer>
            <Label>First name</Label>
            <Input
              name={"emergencyDetails.firstName"}
              value={commonDetails.emergencyDetails.firstName}
              onChange={(e) =>
                updateCommonDetails(e.target.name, e.target.value)
              }
            />
          </FieldContainer>
          <FieldContainer>
            <Label>Last name</Label>
            <Input
              name={"emergencyDetails.lastName"}
              value={commonDetails.emergencyDetails.lastName}
              onChange={(e) =>
                updateCommonDetails(e.target.name, e.target.value)
              }
            />
          </FieldContainer>
          <FieldContainer>
            <Label>Email</Label>
            <Input
              name={"emergencyDetails.email"}
              value={commonDetails.emergencyDetails.email}
              onChange={(e) =>
                updateCommonDetails(e.target.name, e.target.value)
              }
            />
          </FieldContainer>
        </FieldGroupContainer>
        <HorizontalLine />
        <StyledButtonGroup align="left">
          <Button variant={"primary"} onClick={handleSave}>
            Save
          </Button>
          <Button variant={"secondary"} onClick={handleReset}>
            Clear
          </Button>
        </StyledButtonGroup>
        {(bindErrors || []).map((bindError) => (
          <ErrorText>
            Error for DID {bindError.id}: {bindError.message}
          </ErrorText>
        ))}
      </FormContainer>
    </>
  );
};
